import { PURCHASES_ERROR_TYPES } from 'my-purchases/constants/purchasesErrorTypes'
import type { ResponseError } from 'my-purchases/types/purchaseCommon'
import type { Purchase } from 'my-purchases/types/purchaseList'
import { StatusCode } from 'types/statusCodes'

const isStatusCodePartialError = (statusCode: number | null) =>
	statusCode === StatusCode.MULTI_STATUS
const isStatusCodeError = (statusCode: number | null) =>
	statusCode && statusCode !== StatusCode.MULTI_STATUS
const isPurchasesEmpty = (purchases?: Purchase[] | null) =>
	purchases && purchases.length === 0

const pushError = (newError: ResponseError, errors: ResponseError[] = []) => {
	const exists = errors.find(
		(error) =>
			error.errorType === newError.errorType &&
			error.isActivePurchases === newError.isActivePurchases
	)

	if (!exists) {
		errors.push(newError)
	}
}

const getError = (
	error: number | null,
	isActive: boolean,
	errors: ResponseError[],
	isEmpty?: boolean | null
) => {
	// ERRORS INFO
	// Active purchases error !== 207, SERVER_ERROR
	// Inactive purchases error !== 207, SERVER_ERROR
	// Active purchases error === 207, If empty, SERVER_ERROR. If not empty, PARTIAL_ERROR
	// Inactive purchases error === 207, If empty, SERVER_ERROR. If not empty, PARTIAL_ERROR

	if (isStatusCodeError(error)) {
		return pushError(
			{
				errorType: PURCHASES_ERROR_TYPES.SERVER_ERROR,
				isActivePurchases: isActive,
			},
			errors
		)
	}

	if (isStatusCodePartialError(error)) {
		return pushError(
			{
				errorType: isEmpty
					? PURCHASES_ERROR_TYPES.SERVER_ERROR
					: PURCHASES_ERROR_TYPES.PARTIAL_ERROR,
				isActivePurchases: isActive,
			},
			errors
		)
	}

	return null
}

export const getPurchasesErrors = ({
	activePurchases,
	inactivePurchases,
	activePurchasesError,
	inactivePurchasesError,
}: {
	activePurchases?: Purchase[] | null
	inactivePurchases?: Purchase[] | null
	activePurchasesError: number | null
	inactivePurchasesError: number | null
}): ResponseError[] | null => {
	const errors: ResponseError[] = []

	const activePurchasesEmpty = isPurchasesEmpty(activePurchases)
	const inactivePurchasesEmpty = isPurchasesEmpty(inactivePurchases)
	const noErrorCodes = !activePurchasesError && !inactivePurchasesError

	getError(activePurchasesError, true, errors, activePurchasesEmpty)
	getError(inactivePurchasesError, false, errors, inactivePurchasesEmpty)

	// Fetch's OK but all purchases are empty
	if (noErrorCodes && activePurchasesEmpty && inactivePurchasesEmpty) {
		errors.push({
			errorType: PURCHASES_ERROR_TYPES.NO_PURCHASES_FOUND,
			isActivePurchases: true,
		})
	}

	const anyActivePurchasesError = errors.some(
		(x) =>
			x.isActivePurchases && x.errorType === PURCHASES_ERROR_TYPES.SERVER_ERROR
	)
	const anyInactivePurchasesError = errors.some(
		(x) =>
			!x.isActivePurchases && x.errorType === PURCHASES_ERROR_TYPES.SERVER_ERROR
	)

	const bothPurchasesWithError =
		anyActivePurchasesError && anyInactivePurchasesError

	// Both fetch error
	// Empty activePurchases without error && some inactive purchases error
	if (
		bothPurchasesWithError ||
		(!anyActivePurchasesError && activePurchasesEmpty && inactivePurchasesError)
	) {
		pushError(
			{
				errorType: PURCHASES_ERROR_TYPES.FULL_PAGE_ERROR,
			},
			errors
		)
	}

	return errors
}
